<template>
  <div class="bg-white shadow-md rounded-lg mt-6">
    <div class="p-4 border-b flex justify-between items-center">
      <h3 class="font-semibold">{{ $t('smm.ai_logs.title') }}</h3>
      <button @click="load" class="text-sm text-gray-700 hover:underline" :disabled="loading">
        {{ $t('common.refresh') }}
      </button>
    </div>
    <div v-if="loading" class="p-4 text-sm text-gray-500">{{ $t('common.loading') }}</div>
    <div v-else-if="!logs.length" class="p-4 text-sm text-gray-500">{{ $t('smm.ai_logs.empty') }}</div>
    <ul v-else class="divide-y max-h-96 overflow-y-auto">
      <li v-for="log in logs" :key="log.id" class="p-4">
        <button type="button" class="w-full text-left" @click="toggle(log.id)">
          <div class="flex justify-between gap-2">
            <span class="text-sm font-medium">
              #{{ log.id }} · {{ typeLabel(log.type) }}
              <span v-if="log.prompt_template" class="text-gray-500">— {{ log.prompt_template.name }}</span>
            </span>
            <span class="text-xs shrink-0 px-2 py-0.5 rounded" :class="statusClass(log.status)">
              {{ log.status }}
            </span>
          </div>
          <p class="text-xs text-gray-500 mt-1">{{ formatDate(log.created_at) }}</p>
          <p v-if="log.meta?.prefix_applied" class="text-xs text-gray-600 mt-1">
            {{ $t('smm.ai_logs.prefix_applied') }}
          </p>
          <p v-if="log.meta?.content_catalog_appended" class="text-xs text-gray-600 mt-1">
            {{ $t('smm.ai_logs.catalog_count', { n: log.meta.content_catalog_items ?? 0 }) }}
          </p>
          <p v-if="log.meta?.ai_provider" class="text-xs text-gray-500 mt-1">
            {{ $t('smm.ai_logs.provider', { provider: log.meta.ai_provider }) }}
          </p>
          <p v-if="log.meta?.reference_files?.length" class="text-xs text-gray-600 mt-1">
            {{ $t('smm.ai_logs.references', {
              files: log.meta.reference_files.join(', '),
              count: log.meta.reference_images_sent ?? 0,
            }) }}
          </p>
          <p v-else-if="log.meta?.images_count != null && log.type === 'post_content'" class="text-xs text-gray-600 mt-1">
            {{ $t('smm.ai_logs.references_api', { n: log.meta.images_count }) }}
          </p>
          <p v-if="log.meta?.days_created != null" class="text-xs text-gray-600 mt-1">
            {{ $t('smm.ai_logs.schedule_stats', {
              days: log.meta.days_created,
              pins: log.meta.pins_created,
              items: log.meta.items_in_response ?? 0,
            }) }}
          </p>
          <p v-if="log.error_message" class="text-xs text-red-600 mt-1">{{ log.error_message }}</p>
        </button>
        <div v-if="expanded === log.id" class="mt-3 space-y-2">
          <details class="text-xs">
            <summary class="cursor-pointer text-gray-700">{{ $t('smm.ai_logs.prompt_summary') }}</summary>
            <pre class="mt-1 p-2 bg-gray-50 rounded overflow-x-auto whitespace-pre-wrap">{{ log.request_prompt }}</pre>
          </details>
          <details v-if="log.raw_response" class="text-xs">
            <summary class="cursor-pointer text-gray-700">{{ $t('smm.ai_logs.raw_response') }}</summary>
            <pre class="mt-1 p-2 bg-gray-50 rounded overflow-x-auto whitespace-pre-wrap max-h-48">{{ log.raw_response }}</pre>
          </details>
          <details v-if="log.parsed_payload" class="text-xs">
            <summary class="cursor-pointer text-gray-700">{{ $t('smm.ai_logs.parsed_json') }}</summary>
            <pre class="mt-1 p-2 bg-gray-50 rounded overflow-x-auto max-h-48">{{ JSON.stringify(log.parsed_payload, null, 2) }}</pre>
          </details>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  props: {
    socialNetworkId: { type: [String, Number], default: null },
    contentScheduleId: { type: [String, Number], default: null },
    contentItemId: { type: [String, Number], default: null },
    type: { type: String, default: null },
  },
  data: () => ({
    logs: [],
    loading: false,
    expanded: null,
  }),
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const params = {}
        if (this.socialNetworkId) params.social_network_id = this.socialNetworkId
        if (this.contentScheduleId) params.content_schedule_id = this.contentScheduleId
        if (this.contentItemId) params.content_item_id = this.contentItemId
        if (this.type) params.type = this.type
        const { data } = await axios.get('/api/v1/smm/ai-generations', { params })
        this.logs = data.data
      } finally {
        this.loading = false
      }
    },
    toggle(id) {
      this.expanded = this.expanded === id ? null : id
    },
    typeLabel(t) {
      if (t === 'schedule') return this.$t('smm.ai_logs.type_schedule')
      if (t === 'post_content') return this.$t('smm.ai_logs.type_post_content')
      return t
    },
    statusClass(s) {
      return s === 'success' ? 'bg-green-100 text-green-700' : s === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
    },
    formatDate(d) {
      const locale = this.$i18n?.locale === 'ru' ? 'ru-RU' : 'en-US'
      return new Date(d).toLocaleString(locale)
    },
  },
}
</script>
